import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { Packet, Decision } from '@promo/shared';
import { DecisionBadge } from '@/components/ui/DecisionBadge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useState } from 'react';

interface PastAttemptsListProps {
  candidateId: string;
  year: string;
  currentPacketId: string;
  currentAttempt: number;
}

export function PastAttemptsList({ candidateId, year, currentPacketId, currentAttempt }: PastAttemptsListProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery<Packet[], Error>({
    queryKey: ['pastAttempts', candidateId, year],
    queryFn: async () => {
      return get<Packet[]>('/packets', {
        candidateId,
        year,
        includeAttempts: 'true',
      });
    },
  });

  const [expanded, setExpanded] = useState(false);

  if (isLoading || !data) return null;
  if (error) return null;

  const pastAttempts = data
    .filter((p) => p.id !== currentPacketId && (p.attempt ?? 1) < currentAttempt)
    .sort((a, b) => (a.attempt ?? 1) - (b.attempt ?? 1));

  if (!pastAttempts.length) return null;

  return (
    <div className="mb-10">
      {/* Header row with caret */}
      <div
        className="flex items-center justify-between cursor-pointer select-none py-2"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
        }}
      >
        <h2 className="text-lg font-semibold">Past Attempts</h2>
        {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </div>

      {expanded && (
        <div className="overflow-hidden divide-y divide-border rounded-md">
          {pastAttempts.map((p, idx) => (
            <div
              key={p.id}
              className={`flex items-center justify-between px-4 py-3 transition-colors duration-100 hover:bg-accent/40 ${
                idx % 2 !== 0 ? 'bg-gray-100' : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/packets/${p.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate(`/packets/${p.id}`);
              }}
            >
              <div data-testid="past-attempt-label">
                {`Attempt ${p.attempt}`}
              </div>
              <div>
                <DecisionBadge decision={p.decision as Decision | null | undefined} className="capitalize" />
              </div>
              <div className="text-sm text-muted-foreground">
                {p.updatedAt ? format(new Date(p.updatedAt), 'MMM d, yyyy') : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 